/**
 * Dashboard queue processing utilities
 * Handles queue processing logic from Dashboard
 */

import { 
  switchWorkflowSession,
  updateWorkflowSessionState,
  setCurrentlyProcessingSession,
  toggleCollapseFiles,
  removeFromProcessingQueue,
} from '../store/reducers';
import { saveWorkflowLastJobId, loadWorkflowLastJobId } from './workflowSessionStorage';

/**
 * Handle processing the next workflow in queue
 * @param {Object} params
 * @param {string} params.completedSessionId - ID of the completed workflow
 * @param {Object} params.store - Redux store
 * @param {Function} params.dispatch - Redux dispatch
 * @param {Object} params.isProcessingNextInQueueRef - Ref to track if processing next in queue
 * @returns {Promise<boolean>} True if next workflow was started, false otherwise
 */
export const processNextInQueue = async ({
  completedSessionId,
  store,
  dispatch,
  isProcessingNextInQueueRef,
}) => {
  if (isProcessingNextInQueueRef.current) {
    return false;
  }

  const currentState = store.getState();
  const workflowSessionsState = currentState?.workflowSessions;
  const queue = workflowSessionsState?.processingQueue || [];
  let currentlyProcessingId = workflowSessionsState?.currentlyProcessingSessionId;
  const allSessions = workflowSessionsState?.sessions || {};

  // CRITICAL: Always clear currentlyProcessingId if it matches completed workflow or points to any completed workflow (must happen first)
  if (currentlyProcessingId) {
    const currentlyProcessingSession = allSessions[currentlyProcessingId];
    const isCompleted = currentlyProcessingSession?.state?.stepsCompleted || false;
    const isActuallyProcessing = currentlyProcessingSession?.state?.isProcessing || false;
    
    // Clear if: matches completed session ID (primary) or workflow is completed (secondary check)
    const shouldClear = currentlyProcessingId === completedSessionId || isCompleted;
    
    if (shouldClear) {
      dispatch(setCurrentlyProcessingSession(null));
      currentlyProcessingId = null; // Update local variable immediately
    }
  }

  // Backend now supports multiple concurrent jobs - no longer blocking multiple workflows

  if (queue.length === 0) {
    return false;
  }

  const nextWorkflowId = queue[0];

  if (nextWorkflowId && workflowSessionsState?.sessions?.[nextWorkflowId]) {
    const nextSession = workflowSessionsState.sessions[nextWorkflowId];
    const nextIsCompleted = nextSession?.state?.stepsCompleted || false;

    if (nextIsCompleted) {
      dispatch(removeFromProcessingQueue(nextWorkflowId));
      // Recursively process the next one in queue
      return await processNextInQueue({
        completedSessionId: nextWorkflowId,
        store,
        dispatch,
        isProcessingNextInQueueRef,
      });
    }

    isProcessingNextInQueueRef.current = true;

    try {
      dispatch(removeFromProcessingQueue(nextWorkflowId));
      dispatch(switchWorkflowSession(nextWorkflowId));

      await new Promise(resolve => setTimeout(resolve, 500));

      dispatch(setCurrentlyProcessingSession(nextWorkflowId));
      dispatch(updateWorkflowSessionState(nextWorkflowId, {
        isProcessing: true,
        modalClosed: false,
      }));
      dispatch(toggleCollapseFiles());

      window.dispatchEvent(new CustomEvent('startQueueProcessing', { 
        detail: { workflowSessionId: nextWorkflowId }
      }));

      const { default: toast } = await import('react-hot-toast');
      toast.success(`Starting processing for ${nextSession.name}`);

      return true;
    } catch (error) {
      console.error(`❌ [Queue] Error starting next workflow:`, error);
      return false;
    } finally {
      setTimeout(() => {
        isProcessingNextInQueueRef.current = false;
      }, 2000);
    }
  } else {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`⚠️ [Queue] Next workflow ${nextWorkflowId} not found or invalid`);
    }
    return false;
  }
};

/**
 * Handle job ID set event
 * @param {Object} params
 * @param {string} params.jobId - Job ID
 * @param {string} params.eventWorkflowSessionId - Workflow session ID from event
 * @param {Object} params.store - Redux store
 * @param {Function} params.setActiveJobId - Set active job ID state
 * @param {Function} params.dispatch - Redux dispatch
 * @param {Object} params.lastProcessedJobIdRef - Ref to track last processed job ID
 */
export const handleJobIdSet = ({
  jobId,
  eventWorkflowSessionId,
  store,
  setActiveJobId,
  dispatch,
  lastProcessedJobIdRef,
}) => {
  // Get current state to ensure we're using the latest activeWorkflowSessionId
  const currentState = store.getState();
  const currentActiveWorkflowSessionId = currentState?.workflowSessions?.activeSessionId;

  // Only process if there's an active workflow
  if (!currentActiveWorkflowSessionId) {
    return;
  }

  // Only save job ID if it's for the currently active workflow
  if (eventWorkflowSessionId && eventWorkflowSessionId !== currentActiveWorkflowSessionId) {
    // This job ID is for a different workflow, ignore it
    return;
  }

  // If no workflowSessionId in event, assume it's for the active workflow
  const targetWorkflowId = eventWorkflowSessionId || currentActiveWorkflowSessionId;

  // Check if we've already processed this job ID for this workflow
  const workflowJobId = loadWorkflowLastJobId(targetWorkflowId);

  if (workflowJobId === jobId) {
    // This job ID already belongs to this workflow, just set activeJobId if it's the active workflow
    if (targetWorkflowId === currentActiveWorkflowSessionId) {
      setActiveJobId(jobId);
      lastProcessedJobIdRef.current = jobId;
    }
    return;
  }

  // Only set activeJobId if this is for the currently active workflow
  if (targetWorkflowId === currentActiveWorkflowSessionId) {
    setActiveJobId(jobId);
    lastProcessedJobIdRef.current = jobId;
  }

  // Note: Workflow names are kept as "Workflow 1", "Workflow 2", etc. and are not changed to job IDs

  // Clear modalClosed flag when new job starts (allows modal to show again if processing starts from queue)
  dispatch(updateWorkflowSessionState(targetWorkflowId, {
    modalClosed: false,
  }));

  // Save job ID to per-workflow sessionStorage for the target workflow
  saveWorkflowLastJobId(targetWorkflowId, jobId);
};

/**
 * Handle job ID clear event
 * @param {Object} params
 * @param {Function} params.setActiveJobId - Set active job ID state
 * @param {Object} params.lastProcessedJobIdRef - Ref to track last processed job ID
 */
export const handleJobIdClear = ({
  setActiveJobId,
  lastProcessedJobIdRef,
}) => {
  setActiveJobId(null);
  lastProcessedJobIdRef.current = null;
  sessionStorage.removeItem('activeJobId');
};

